import { AchFileActivityModelDto } from '@treasury/api/channel';
import { DomainEntity } from '../shared';
import type { AchFileUploadStatus, AchRecentFile } from './ach-batch-import.entity';

export class AchFileActivity extends DomainEntity<AchFileActivityModelDto> {
    public createDefault(): this {
        return new AchFileActivity({
            id: 0,
            updatedDate: '',
            fileName: '',
            fileId: 0,
            fileArchiveId: 0,
            hasValidationError: false,
            isActive: false,
            uploadDate: new Date().toISOString(),
            totalBatches: 0,
            approvedCount: 0,
            pendingApprovalCount: 0,
            rejectedCount: 0,
            expiredCount: 0,
            debitAmount: 0,
            creditAmount: 0,
            isExpired: false,
            isProcessable: false,
        }) as this;
    }

    public get fileName() {
        return this.dto.fileName;
    }

    public get fileId() {
        return this.dto.fileId;
    }

    public get fileArchiveId() {
        return this.dto.fileArchiveId;
    }

    public get validationError() {
        return this.dto.validationError;
    }

    public get hasValidationError() {
        return this.dto.hasValidationError;
    }

    public get isActive() {
        return this.dto.isActive;
    }

    public get achCompanyName() {
        return this.dto.achCompanyName;
    }

    public get fileSize() {
        return this.dto.fileSize;
    }

    public get source() {
        return this.dto.source;
    }

    public get uploadDate() {
        return this.dto.uploadDate;
    }

    public get totalBatches() {
        return this.dto.totalBatches;
    }

    public get approvedCount() {
        return this.dto.approvedCount;
    }

    public get pendingApprovalCount() {
        return this.dto.pendingApprovalCount;
    }

    public get rejectedCount() {
        return this.dto.rejectedCount;
    }

    public get expiredCount() {
        return this.dto.expiredCount;
    }

    public get debitAmount() {
        return this.dto.debitAmount;
    }

    public get creditAmount() {
        return this.dto.creditAmount;
    }

    public get status() {
        return this.dto.status;
    }

    public get processedBy() {
        return this.dto.processedBy;
    }

    public get processedDate() {
        return this.dto.processedDate;
    }

    public get isExpired() {
        return this.dto.isExpired;
    }

    public get isProcessable() {
        return this.dto.isProcessable;
    }

    public get permissions() {
        return this.dto.permissions;
    }

    public toRecentFile(): AchRecentFile {
        return {
            fileName: this.dto.fileName ?? '',
            fileStatus: this.mapToUploadStatus(),
            uploadedDateTime: this.dto.uploadDate ?? '',
        };
    }

    private mapToUploadStatus(): AchFileUploadStatus {
        const status = this.dto.status?.toLowerCase() ?? '';
        const statusMap: Record<string, AchFileUploadStatus> = {
            uploaded: 'Uploaded',
            queued: 'Queued',
            'in progress': 'In Progress',
            processing: 'In Progress',
            done: 'Done',
            complete: 'Done',
            stalled: 'Stalled',
            error: 'Error',
            failed: 'Error',
        };
        return statusMap[status] ?? 'Uploaded';
    }

    /**
     * Determines if the file is processed based on transmission status
     */
    public get isProcessed() {
        const isTransmitted = this.source === 'System';
        return !(isTransmitted && !this.processedDate);
    }

    /**
     * Gets the effective processed date (uses upload date for non-transmitted files)
     */
    public get effectiveProcessedDate() {
        const isTransmitted = this.source === 'System';
        return isTransmitted ? this.processedDate : this.uploadDate;
    }

    /**
     * Gets the effective processed by (uses source for non-transmitted files)
     */
    public get effectiveProcessedBy() {
        const isTransmitted = this.source === 'System';
        return isTransmitted ? this.processedBy : this.source;
    }
}
